import { mutualOfOmahaMortgageBrentwoodTn } from "../data/locations";
import { mutualMortgagePages } from "./mutualMortgagePages";
import { personSeoPages } from "./personSeoPages";

const commonResources = [
  {
    label: "CFPB complaint portal",
    href: "https://www.consumerfinance.gov/complaint/",
  },
  {
    label: "NMLS Consumer Access",
    href: "https://www.nmlsconsumeraccess.org/",
  },
  {
    label: "LinkedIn profile: Robert Hillman Shell",
    href: "https://www.linkedin.com/in/rob-shell-93622325/",
  },
  {
    label: "Mutual of Omaha Mortgage (Brentwood, TN) location page",
    href: mutualOfOmahaMortgageBrentwoodTn.locationPageUrl,
  },
  {
    label: "Mutual of Omaha Mortgage (Brentwood, TN) office address (Google Maps)",
    href: mutualOfOmahaMortgageBrentwoodTn.mapsUrl,
  },
  {
    label: "Regulation Z (12 CFR Part 1026)",
    href: "https://www.consumerfinance.gov/rules-policy/regulations/1026/",
  },
];

const mutualOfOmahaBrentwoodAddress = `${mutualOfOmahaMortgageBrentwoodTn.address.streetAddress}, ${mutualOfOmahaMortgageBrentwoodTn.address.addressLocality}, ${mutualOfOmahaMortgageBrentwoodTn.address.addressRegion} ${mutualOfOmahaMortgageBrentwoodTn.address.postalCode}`;

const coreTopicPages = [
  {
    slug: "/complaint-overview",
    title: "Complaint Overview",
    linkTitle: "Complaint Overview",
    metaTitle: "Complaint Overview | CFPB Mortgage Complaint",
    description:
      "Overview of the CFPB complaint, Mutual of Omaha Mortgage response, and complainant rebuttal involving two investment-property loans.",
    subheading:
      "Summary of the filing position, legal framework, and disputed issues.",
    sections: [
      {
        heading: "Filing position and core allegations",
        paragraphs: [
          "The complaint was filed with the Consumer Financial Protection Bureau and cites Dodd-Frank UDAAP authority, TILA, RESPA, and Regulation Z.",
          "It alleges steering into a higher-cost DSCR/non-QM interest-only product, incomplete product-option presentation, and disclosure timing problems in the lead-up to closing.",
        ],
      },
      {
        heading: "Mutual of Omaha Mortgage response position",
        paragraphs: [
          "Mutual states the borrower sought financing for two investment properties and did not meet personal-income qualification for traditional conventional underwriting at the time of application.",
          "Mutual states the DSCR program through NewFi Lending was explained, accepted by the borrower, and properly disclosed in business-purpose transactions that the company considers exempt under 12 CFR 1026.3(a).",
        ],
      },
      {
        heading: "Complainant rebuttal position",
        paragraphs: [
          "The rebuttal states the DSCR path was presented as the only practical option and that requested side-by-side alternatives and conventional pricing analysis were not provided during the decision window.",
          "The rebuttal further states that timing pressure tied to earnest money reduced meaningful product choice and that later rate and product information supports additional regulatory review.",
        ],
      },
      {
        heading: "Current dispute status",
        paragraphs: [
          "The two sides disagree on suitability, option availability, disclosure sufficiency, and the legal effect of business-purpose classification.",
          "The complainant requests additional regulator review and production of lock-date pricing records, internal eligibility analysis, disclosure logs, and compensation documentation.",
        ],
      },
    ],
    resources: commonResources,
  },
  {
    slug: "/parties-entities",
    title: "Parties and Entities",
    linkTitle: "Parties and Entities",
    metaTitle: "Parties and Entities | CFPB Mortgage Complaint",
    description:
      "Key parties and entities referenced in the complaint, company response, and rebuttal, including Mutual of Omaha Mortgage and NewFi Lending.",
    subheading: "Who is named and how each party is described.",
    sections: [
      {
        heading: "Complainant",
        paragraphs: [
          "The complainant is the borrower identified in the complaint record.",
          "The complaint materials state the borrower acted through an LLC.",
        ],
      },
      {
        heading: "Respondents and loan participants",
        paragraphs: [
          "Robert Hillman Shell (NMLS ID #2226086) is identified as the mortgage professional involved in the transactions.",
          "Mutual of Omaha Mortgage and NewFi Lending are listed in the dispute materials as broker/originator-side and funding channel participants.",
        ],
      },
      {
        heading: "Mutual of Omaha Mortgage (Brentwood, TN)",
        paragraphs: [`Public office listing: ${mutualOfOmahaBrentwoodAddress}.`],
      },
      {
        heading: "Other entities referenced",
        list: [
          "Planet Home Lending: identified as later assignee/servicer after origination.",
          "Capital Fund One: cited in comparative loan-option references from later transactions.",
          "CFPB, HUD RESPA Enforcement Office, and Tennessee Attorney General: named in requested regulatory action.",
        ],
      },
    ],
    resources: commonResources,
  },
  {
    slug: "/loan-timeline",
    title: "Loan Timeline",
    linkTitle: "Loan Timeline",
    metaTitle: "Loan Timeline | CFPB Mortgage Complaint",
    description:
      "Timeline of application, disclosures, funding channel, transfer-related issues, and later comparison evidence cited by both sides.",
    subheading: "Key dates and events cited in the complaint and response.",
    sections: [
      {
        heading: "Application and initial loan path",
        list: [
          "October 18, 2023: Unit 514 application date cited by both sides.",
          "December 15, 2023: Unit 512 application date cited by both sides.",
          "Mutual states the DSCR path was suitable based on borrower profile and investment-property cash-flow qualification.",
          "Complainant states meaningful alternatives were requested and not presented during the active decision window.",
        ],
      },
      {
        heading: "Disclosure dates cited by Mutual",
        list: [
          "December 12, 2023: initial package date cited by Mutual for Unit 514.",
          "December 18, 2023: initial package date cited by Mutual for Unit 512.",
          "Mutual states sample mortgage comparison materials were provided in those disclosure packages.",
          "Complainant disputes practical sufficiency and timing of disclosure delivery relative to closing decisions.",
        ],
      },
      {
        heading: "Closing pressure and choice context (disputed)",
        paragraphs: [
          "The rebuttal states the borrower proceeded under earnest-money risk and compressed timelines, not as unrestricted product preference.",
          "Mutual disputes coercion and states communications show informed agreement with the selected product.",
        ],
      },
      {
        heading: "Post-closing references",
        paragraphs: [
          "Both sides reference NewFi Lending in funding/broker-channel context and later servicing/transfer issues involving Planet Home Lending.",
          "Mutual disputes the use of 2024-2025 rate references as proof of 2023 product availability; complainant cites those later materials as comparative context for regulator review.",
        ],
      },
    ],
    resources: commonResources,
  },
  {
    slug: "/company-response-rebuttal",
    title: "Company Response and Complainant Rebuttal",
    linkTitle: "Response and Rebuttal",
    metaTitle: "Company Response and Rebuttal | CFPB Mortgage Complaint",
    description:
      "Side-by-side summary of Mutual of Omaha Mortgage's CFPB response and the complainant's rebuttal points for regulator and media review.",
    subheading:
      "Structured summary of both positions with disputed points clearly separated.",
    sections: [
      {
        heading: "Mutual of Omaha Mortgage response (as stated)",
        list: [
          "Borrower sought financing for two investment properties and did not meet conventional personal-income qualification at the time, according to Mutual.",
          "DSCR 30-year interest-only product through NewFi Lending was discussed and selected after borrower agreement, according to Mutual.",
          "Initial disclosure packages were issued on December 12, 2023 (Unit 514) and December 18, 2023 (Unit 512), including sample mortgage comparisons, according to Mutual.",
          "Loans were business-purpose transactions and therefore exempt from cited consumer-disclosure provisions under 12 CFR 1026.3(a), according to Mutual.",
          "No evidence of coercion, steering, inflated charges, or improper compensation was found in Mutual's internal review, according to Mutual.",
        ],
      },
      {
        heading: "Complainant rebuttal (as stated)",
        list: [
          "DSCR was presented as the only practical option, while requested side-by-side alternatives and conventional analyses were not provided during the active decision window.",
          "Proceeding message was sent under risk of earnest-money loss and compressed timeline pressure, not full optionality.",
          "Business-purpose classification does not eliminate anti-steering obligations or UDAAP scrutiny when product choice and timing are disputed.",
          "Later rate and product information is cited as comparative evidence that materially different options may have existed and should be reviewed.",
          "Transfer-notice clarity and origination-side transparency remain unresolved in the rebuttal.",
        ],
      },
      {
        heading: "Regulator-focused review items requested",
        list: [
          "Lock-date rate sheets and product menus available for both files.",
          "Internal notes regarding conventional eligibility analysis and decline rationale.",
          "Full disclosure delivery logs with send timestamps and acknowledgments.",
          "Compensation structure details for brokered vs. in-house products.",
          "Transfer/servicing notice chain across Mutual, NewFi, and Planet Home Lending.",
        ],
      },
    ],
    resources: commonResources,
  },
  {
    slug: "/allegations-factual-basis",
    title: "Allegations and Factual Basis",
    linkTitle: "Allegations and Factual Basis",
    metaTitle: "Allegations and Factual Basis | CFPB Mortgage Complaint",
    description:
      "Summary of factual allegations and contested issues, including option presentation, steering concerns, and disclosure timing disputes.",
    subheading: "Key allegations stated by the complainant.",
    sections: [
      {
        heading: "Allegations listed",
        list: [
          "Only one non-QM/DSCR interest-only path was effectively presented during origination (complaint position).",
          "Requests for alternative products, pricing, and comparative analysis were not fulfilled during the decision window (complaint position).",
          "Borrower was directed toward a higher-cost product without complete option transparency (complaint position).",
          "Disclosure timing did not provide a meaningful pre-closing review window under practical circumstances (complaint position).",
          "Transfer/servicing transparency and timing around Planet Home Lending remain disputed (complaint position).",
          "Later 2024-2025 option/rate references are cited as comparative evidence of potentially lower-cost alternatives (complaint position).",
        ],
      },
      {
        heading: "Company position on these allegations",
        list: [
          "Mutual denies coercion and steering and states the selected product matched the borrower's qualification profile at application time.",
          "Mutual states required disclosures for the business-purpose classification were provided and that allegations based on consumer-purpose rules are misapplied.",
          "Mutual states it did not identify excessive fees, predatory conduct, or unfair/deceptive behavior in its review.",
        ],
      },
    ],
    resources: commonResources,
  },
  {
    slug: "/legal-violations",
    title: "Legal Violations Alleged",
    linkTitle: "Legal Violations",
    metaTitle: "Legal Violations | CFPB Mortgage Complaint",
    description:
      "Federal statutes and regulations cited in the complaint and response, including anti-steering, business-purpose exemption, and UDAAP issues.",
    subheading: "Federal legal frameworks cited by both sides.",
    sections: [
      {
        heading: "Regulation Z anti-steering (12 CFR 1026.36(e))",
        paragraphs: [
          "The complaint and rebuttal assert that available lower-cost options were not meaningfully presented and that the selected product carried higher long-term cost.",
          "Mutual denies steering and states product selection reflected suitability analysis for the borrower's transaction profile.",
        ],
      },
      {
        heading: "Regulation Z disclosure framework (12 CFR 1026.19(e))",
        paragraphs: [
          "The complaint challenges whether disclosures and comparative information were delivered in time for practical, informed choice before closing.",
          "Mutual states disclosures were compliant for the transaction type and cites specific package dates for both units.",
        ],
      },
      {
        heading: "Business-purpose exemption (12 CFR 1026.3(a))",
        paragraphs: [
          "Mutual relies on business-purpose classification and signed occupancy/business-purpose affidavits to support exemption from certain consumer-disclosure provisions.",
          "The rebuttal argues exemption status does not eliminate anti-steering or UDAAP review where product presentation and timing are contested.",
        ],
      },
      {
        heading: "Transfer notice framework (12 CFR 1026.39)",
        paragraphs: [
          "The complaint raises transfer-notice transparency and timing concerns tied to servicing/ownership changes.",
          "Mutual states it acted as broker and directs post-funding transfer questions to NewFi Lending.",
        ],
      },
      {
        heading: "RESPA Section 8 and Dodd-Frank UDAAP",
        paragraphs: [
          "The complaint invokes RESPA anti-kickback and Dodd-Frank UDAAP authority for regulator review.",
          "Mutual disputes any evidence of referral abuse, excessive fees, misrepresentation, or unfair/deceptive practices.",
        ],
      },
    ],
    resources: commonResources,
  },
  {
    slug: "/damages-harm",
    title: "Damages and Harm",
    linkTitle: "Damages and Harm",
    metaTitle: "Damages and Harm | CFPB Mortgage Complaint",
    description:
      "Financial impacts alleged in the complaint and rebuttal, including higher borrowing costs and reduced investment performance.",
    subheading: "Alleged financial impacts described in the complaint.",
    sections: [
      {
        heading: "Impacts described",
        list: [
          "Higher borrowing cost from the selected product relative to alternatives the complainant states should have been explored.",
          "Reduced long-term investment returns due to interest structure and loan-cost profile (complaint position).",
          "Opportunity-cost harm from not receiving complete option/pricing transparency before closing (complaint position).",
          "Decision pressure linked to earnest-money risk and compressed timelines (complaint position).",
        ],
      },
      {
        heading: "Dispute status",
        paragraphs: [
          "Mutual disputes the damages narrative and states its review did not identify excessive origination, discount-fee abuse, or overcharges for the selected product.",
        ],
      },
    ],
    resources: commonResources,
  },
  {
    slug: "/requested-action",
    title: "Requested Federal Action",
    linkTitle: "Requested Federal Action",
    metaTitle: "Requested Federal Action | CFPB Mortgage Complaint",
    description:
      "Requested actions directed to the CFPB and related authorities, including further investigation and record production.",
    subheading: "What the complaint and rebuttal ask regulators to do.",
    sections: [
      {
        heading: "Requested regulator actions",
        list: [
          "Conduct deeper investigation into product-option presentation, steering concerns, and disclosure timing for both files.",
          "Evaluate applicability of 12 CFR 1026.36(e), 1026.19(e), 1026.39, RESPA, and UDAAP standards to the documented sequence.",
          "Require production of lock-date pricing, internal eligibility analysis, compensation structure records, and disclosure delivery logs.",
          "Consider coordination with Tennessee Attorney General and HUD RESPA enforcement channels where appropriate.",
        ],
      },
    ],
    resources: commonResources,
  },
  {
    slug: "/exhibits-evidence",
    title: "Exhibits and Evidence",
    linkTitle: "Exhibits and Evidence",
    metaTitle: "Exhibits and Evidence | CFPB Mortgage Complaint",
    description:
      "Complaint exhibits, company response attachments, rebuttal materials, and records requested for independent review.",
    subheading: "Documents referenced as supporting exhibits.",
    sections: [
      {
        heading: "Company response attachments (as referenced)",
        list: [
          "Attachment A: Sample Mortgage Comparisons.",
          "Attachment B: Business Purpose and Occupancy Affidavits.",
          "Closing-file disclosures and fee/compensation review references cited in the company response narrative.",
        ],
      },
      {
        heading: "Rebuttal-supporting references",
        list: [
          "Email/communication context on requested alternatives and decision pressure timing.",
          "Later 2024-2025 option/rate references cited as comparative context.",
          "Statements regarding transfer-notice clarity and servicing transparency.",
        ],
      },
      {
        heading: "Records still requested for third-party validation",
        list: [
          "Lock-date rate sheets and product matrices for both property files.",
          "Internal underwriting/eligibility notes for conventional alternatives.",
          "Disclosure delivery logs with full timestamps and acknowledgments.",
          "Compensation documentation for brokered and in-house product pathways.",
        ],
      },
    ],
    resources: commonResources,
  },
  {
    slug: "/mutual-response-detailed",
    title: "Mutual Response: Detailed Assertions",
    linkTitle: "Mutual Response (Detailed)",
    metaTitle: "Mutual of Omaha Mortgage Response Summary | CFPB Mortgage Complaint",
    description:
      "Detailed summary of Mutual of Omaha Mortgage's written response, including DSCR suitability, disclosure claims, exemption arguments, and fee findings.",
    subheading: "Detailed extraction of the company's response points.",
    sections: [
      {
        heading: "Transaction framing stated by Mutual",
        paragraphs: [
          "Mutual states the borrower requested financing for two investment properties at 700 N Dupont Ave, Units 514 and 512 in Madison, Tennessee.",
          "Mutual cites application dates of October 18, 2023 (Unit 514) and December 15, 2023 (Unit 512).",
        ],
      },
      {
        heading: "Suitability and product-selection explanation",
        list: [
          "Mutual states borrower personal-income profile did not meet conventional qualification expectations at the time of application.",
          "Mutual states a 30-year interest-only DSCR program through NewFi Lending was discussed and selected.",
          "Mutual states borrower communications confirmed readiness to proceed with the DSCR option.",
        ],
      },
      {
        heading: "Disclosure and exemption assertions",
        list: [
          "Mutual states initial package dates were December 12, 2023 (Unit 514) and December 18, 2023 (Unit 512).",
          "Mutual states sample mortgage comparisons were included in disclosures.",
          "Mutual cites 12 CFR 1026.3(a) business-purpose exemption and signed occupancy/business-purpose affidavits.",
          "Mutual states final closing disclosures were included in each closing file.",
        ],
      },
      {
        heading: "Pricing, timing, and compensation assertions",
        list: [
          "Mutual disputes that 2024-2025 rate references are reliable proof of 2023 availability due to market and borrower-profile changes.",
          "Mutual states there was no incentive to steer because brokered DSCR compensation was lower than conventional in-house compensation.",
          "Mutual states file review found no excessive origination fees, discount abuse, or interest overcharges.",
        ],
      },
      {
        heading: "Transfer-related position",
        paragraphs: [
          "Mutual states it acted as broker and did not control post-funding transfer handling, directing transfer questions to NewFi Lending.",
          "Mutual concludes the files were originated and closed in compliance with applicable law.",
        ],
      },
    ],
    resources: commonResources,
  },
  {
    slug: "/rebuttal-detailed",
    title: "Complainant Rebuttal: Detailed Issues",
    linkTitle: "Rebuttal (Detailed)",
    metaTitle: "Complainant Rebuttal and Unresolved Issues | CFPB Mortgage Complaint",
    description:
      "Detailed summary of the complainant's rebuttal, including steering concerns, earnest-money pressure, and requested record production.",
    subheading: "Detailed extraction of unresolved points raised in rebuttal.",
    sections: [
      {
        heading: "Product steering and option transparency concerns",
        list: [
          "Rebuttal states side-by-side loan alternatives and written conventional pricing were requested but not delivered in the active decision window.",
          "Rebuttal states no formal comparative borrowing-cost analysis was provided before commitment decisions.",
          "Rebuttal asserts the DSCR path functioned as the only viable option presented during origination.",
        ],
      },
      {
        heading: "Constructive pressure and consent context",
        paragraphs: [
          "Rebuttal states agreement-to-proceed language cannot be isolated from context because closing failure risked earnest-money loss.",
          "The rebuttal cites compressed timing and asserts that meaningful choice was constrained when only one practical path was offered.",
        ],
      },
      {
        heading: "Business-purpose exemption limits argued in rebuttal",
        list: [
          "Rebuttal agrees loans were investment-purpose but argues exemption status does not eliminate anti-steering or UDAAP scrutiny.",
          "Rebuttal argues meaningful disclosure requires enough time to evaluate alternatives and seek independent advice before closing.",
          "Rebuttal states signed affidavits do not cure potential misrepresentation in product-option presentation.",
        ],
      },
      {
        heading: "Later rate-sheet relevance argued in rebuttal",
        list: [
          "Rebuttal cites later rate and product information as comparative evidence that materially different options existed in lender channels.",
          "Rebuttal argues those later materials justify audit of lock-date availability and contemporaneous product menus.",
        ],
      },
      {
        heading: "Transfer transparency and pattern concerns",
        list: [
          "Rebuttal states transfer timing and notice clarity remain unresolved despite broker-lender role distinctions.",
          "Rebuttal identifies a combined pattern of option limits, timing pressure, and higher-cost outcomes as requiring deeper review.",
        ],
      },
      {
        heading: "Requested review and production items",
        list: [
          "All lock-date rate sheets and product matrices for both files.",
          "Underwriting notes addressing conventional eligibility at application time.",
          "Compensation structure records for the involved loan channel and personnel.",
          "Complete disclosure delivery logs with timestamps and acknowledgments.",
        ],
      },
    ],
    resources: commonResources,
  },
  {
    slug: "/document-requests",
    title: "Requested Records and Audit Trail",
    linkTitle: "Requested Records",
    metaTitle: "Requested Mortgage Records and Audit Trail | CFPB Mortgage Complaint",
    description:
      "Checklist of records requested for third-party validation, including lock-date pricing, disclosure logs, underwriting notes, and compensation records.",
    subheading: "Document production checklist for independent regulatory review.",
    sections: [
      {
        heading: "Requested from originating/broker channels",
        list: [
          "Lock-date rate sheets and full product menus available for both files.",
          "Any matrix or policy used to determine suitability of DSCR vs. conventional alternatives.",
          "Internal communications documenting product recommendations and borrower-option requests.",
        ],
      },
      {
        heading: "Requested from underwriting and compliance files",
        list: [
          "Underwriting notes and conditions tied to conventional eligibility analysis.",
          "Disclosure generation and delivery audit trail with exact timestamps.",
          "Signed acknowledgments and document-transmission evidence for each disclosure package.",
        ],
      },
      {
        heading: "Requested compensation and fee records",
        list: [
          "Compensation schedules for brokered DSCR channels and in-house alternatives.",
          "Loan-level compensation records associated with each subject file.",
          "Fee-breakdown workpapers used for internal compliance review.",
        ],
      },
      {
        heading: "Why these records matter",
        paragraphs: [
          "These records test whether options were fully and timely presented, whether disclosures were meaningful before closing decisions, and whether compensation structures created channel bias.",
          "The records also help align disputed narratives with objective timestamps, pricing snapshots, and decision documentation.",
        ],
      },
    ],
    resources: commonResources,
  },
];

const infoTopicSlugs = new Set([
  "/source-index-reading-guide",
  "/source-ma-mutual-omaha-mortgage-2025",
  "/source-sc-mutual-omaha-insurance-2025",
  "/source-sd-united-omaha-life-2024",
  "/source-ct-mutual-omaha-kickbacks-2008",
]);

const mutualMortgagePageSlugs = new Set(mutualMortgagePages.map((page) => page.slug));

function withTemplate(page) {
  const contexts = new Set(page.contexts || []);

  if (mutualMortgagePageSlugs.has(page.slug)) {
    contexts.add("mutual-mortgage");
  }

  return {
    ...page,
    template: infoTopicSlugs.has(page.slug) ? "info" : "summary",
    contexts: [...contexts],
  };
}

export const topicPages = [...coreTopicPages, ...mutualMortgagePages, ...personSeoPages].map(
  withTemplate
);
